<template>
    <nav
        :class="[
            'bg-surface-0',
            'flex',
            'h-screen',
            'w-fit',
            'flex-col',
            'items-center',
            'justify-between',
        ]"
    >
        <div>
            <a class="mb-2 block cursor-pointer px-4 pt-4" href="" @click="reload">
                <BoServiceLogo />
                <div data-testid="nav-project-name" class="mt-2 w-full text-[1.25rem] font-bold">
                    {{ $t('project-name') }}
                </div>
            </a>
            <Menu
                :model="items"
                class="m-0 p-0 ps-1"
                :pt="{
                    list: {
                        class: 'h-0 p-0',
                    },
                }"
            >
                <template #item="{ item, props }">
                    <Button
                        variant="link"
                        v-ripple
                        class="flex w-full justify-start"
                        :class="[
                            item.route === route.name ? 'bg-surface-100 font-semibold' : '',
                            item.icon,
                            !!item?.command && item?.label !== startLabel ? 'ps-8' : '',
                        ]"
                        v-bind="props.action"
                        :data-testid="item?.testid"
                    >
                        {{ item.label }}
                    </Button>
                </template>
            </Menu>
        </div>
        <a href="" @click="reload" class="text-grey mb-4 text-xs"
            >{{ $t('menu.version') }} {{ FE_VERSION }}</a
        >
    </nav>
</template>

<script setup lang="ts">
    import { useGetOrderList, useGetProcessedOrdersList } from '@/api/order';
    import { useGetSpecialCasePartnerList } from '@/api/specialCase';
    import { FE_VERSION } from '@/constants';
    import { RouteNames } from '@/router/routeNames';
    import useAuthStore from '@/stores/useAuthStore';
    import useLocationsStore from '@/stores/useLocationsStore';
    import { storeToRefs } from 'pinia';
    import type { MenuItem } from 'primevue/menuitem';
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useRoute, useRouter } from 'vue-router';

    const { t } = useI18n();
    const authStore = useAuthStore();
    const router = useRouter();
    const route = useRoute();
    const startLabel = t('menu.start');
    const ordersToProcessLabel = t('menu.ordersToProcess');
    const ordersProcessedLabel = t('menu.ordersProcessed');
    const creditNoteLabel = t('menu.creditNote');
    const creditNoteTransferredLabel = t('menu.transferred');
    const settingsLabel = t('menu.settings');
    const specialCasesLabel = t('menu.specialCases');
    const administratorLabel = t('menu.administrator');
    const creditNoteProcessingLabel = t('menu.creditNoteProcessing');

    const { selected } = storeToRefs(useLocationsStore());
    const { isAdmin } = storeToRefs(authStore);
    const { data: orders, isError: isErrorOrders } = useGetOrderList();
    const { data: processedOrders, isError: isErrorProcessedOrders } = useGetProcessedOrdersList();
    const { data: specialCaseOrders, isError: isErrorSpecialCasesOrders } =
        useGetSpecialCasePartnerList();

    const formatMenuLabel = (label: string, count: number | undefined) =>
        count === undefined ? label : `${label} (${count})`;

    const items = computed<MenuItem[]>(() => [
        {
            separator: true,
        },
        {
            label: startLabel,
            route: RouteNames.Index,
            testid: 'nav-start',
            command: () => {
                router.push({ name: RouteNames.Index }).catch(console.error);
            },
        },
        {
            separator: true,
        },
        {
            label: t('order.default'),
            items: [
                {
                    label: formatMenuLabel(ordersToProcessLabel, orders.value?.length),
                    route: RouteNames.ToProcess,
                    testid: 'nav-orders-to-process',
                    disabled:
                        !selected.value?.length || (!orders.value?.length && !isErrorOrders.value),
                    command: () => {
                        router.push({ name: RouteNames.ToProcess }).catch(console.error);
                    },
                },
                {
                    label: formatMenuLabel(ordersProcessedLabel, processedOrders.value?.totalCount),
                    route: RouteNames.Processed,
                    testid: 'nav-orders-processed',
                    disabled: !processedOrders.value?.totalCount && !isErrorProcessedOrders.value,
                    command: () => {
                        router.push({ name: RouteNames.Processed }).catch(console.error);
                    },
                },
            ],
        },
        {
            label: creditNoteLabel,
            items: [
                {
                    label: creditNoteTransferredLabel,
                    route: RouteNames.CreditNote,
                    testid: 'nav-credit-note-transferred',
                    command: () => {
                        router.push({ name: RouteNames.CreditNote }).catch(console.error);
                    },
                },
            ],
        },
        {
            label: settingsLabel,
            items: [
                {
                    label: formatMenuLabel(specialCasesLabel, specialCaseOrders.value?.totalCount),
                    route: RouteNames.SpecialCases,
                    testid: 'nav-special-cases',
                    disabled: isErrorSpecialCasesOrders.value,
                    command: () => {
                        router.push({ name: RouteNames.SpecialCases }).catch(console.error);
                    },
                },
            ],
        },
        ...(isAdmin.value
            ? [
                  {
                      label: administratorLabel,
                      items: [
                          {
                              label: creditNoteProcessingLabel,
                              route: RouteNames.Administrator,
                              testid: 'nav-admin-credit-note-processing',
                              command: () => {
                                  router
                                      .push({ name: RouteNames.Administrator })
                                      .catch(console.error);
                              },
                          },
                      ],
                  },
              ]
            : []),
        {
            separator: true,
        },
        {
            label: t('menu.account'),
            items: [
                {
                    label: t('auth.logout'),
                    testid: 'logout',
                    command: () => {
                        authStore.logout();
                    },
                },
            ],
        },
        {
            separator: true,
        },
    ]);

    const reload = () => {
        router.go(0);
    };
</script>
